/**
 * ============================================================================
 * BUSINESS LOGIC: GESTIÓN DE ZONAS HORARIAS Y FILTROS TEMPORALES (HU-02, HU-03, HU-04)
 * ============================================================================
 * Manejo de conversiones precisas entre hora local de plaza y UTC (ISO 8601),
 * presets de filtrado por partido, validaciones cronológicas y descarga de reportes (Excel, ZIP).
 */

import {
  GetMessagesExcelBlob,
  GetFanCamZipBlob,
  TriggerMasterZipGeneration,
  GetMasterZipStatus,
  DownloadReadyReportBlob,
} from '../../DL/Admin/Reports';

/**
 * Listado de zonas horarias oficiales de las plazas de la Liga / eventos.
 * Soporta acceso como arreglo iterable y como diccionario por clave de zona horaria.
 */
const OFFICIAL_TIMEZONES = [
  { value: 'America/Mexico_City', label: 'Centro: CDMX, GDL, MTY (UTC-6)' },
  { value: 'America/Tijuana', label: 'Noroeste: Tijuana Xolos (UTC-8)' },
  { value: 'America/Hermosillo', label: 'Pacífico: Sonora (UTC-7)' },
  { value: 'America/Mazatlan', label: 'Pacífico: Mazatlán (UTC-7)' },
  { value: 'America/Ciudad_Juarez', label: 'Frontera Norte: Juárez (UTC-6)' },
  { value: 'America/Cancun', label: 'Sureste: Quintana Roo (UTC-5)' },
  { value: 'UTC', label: 'UTC (Tiempo Universal Coordinado)' },
];

// Permite indexar directamente OFFICIAL_TIMEZONES['America/Mexico_City']
OFFICIAL_TIMEZONES.forEach((tz) => {
  OFFICIAL_TIMEZONES[tz.value] = tz.label;
});

/**
 * Obtiene la zona horaria del navegador del cliente mediante la API estándar de Intl.
 * Si no está disponible o falla, aplica fallback a 'America/Mexico_City'.
 * @returns {string} Identificador IANA de la zona horaria
 */
const GetAutodetectedTimezone = () => {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (detected && typeof detected === 'string') {
      return detected;
    }
  } catch (error) {
    // Fallback en entornos donde Intl no proporcione la zona
  }
  return 'America/Mexico_City';
};

/**
 * Helper interno: Extrae componentes de fecha/hora para una fecha en una zona horaria dada
 * y retorna su timestamp simulado en UTC.
 */
const getPartsUtc = (dateObj, targetTz) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: targetTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(dateObj).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
};

/**
 * Formatea un objeto Date al formato de entrada local 'YYYY-MM-DDTHH:mm' en la zona indicada.
 * @param {Date} date - Objeto Date en memoria
 * @param {string} timeZone - Zona horaria destino
 * @returns {string} Formato 'YYYY-MM-DDTHH:mm'
 */
const formatInTimeZone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

/**
 * Convierte una fecha y hora local ('YYYY-MM-DDTHH:mm' o 'YYYY-MM-DDTHH:mm:ss')
 * interpretada en la zona horaria indicada a su marca ISO 8601 UTC exacta (ej. '2026-09-08T18:00:00.000Z').
 * @param {string} dateTimeLocalStr - Cadena de fecha y hora local
 * @param {string} timeZone - Zona horaria de referencia
 * @returns {string|null} Marca ISO 8601 UTC o null si el formato es inválido
 */
const ConvertLocalToUtc = (dateTimeLocalStr, timeZone) => {
  if (!dateTimeLocalStr || typeof dateTimeLocalStr !== 'string') return null;

  const tz = timeZone || GetAutodetectedTimezone();
  const [datePart, timePart] = dateTimeLocalStr.split('T');
  if (!datePart || !timePart) return null;

  const [year, month, day] = datePart.split('-').map(Number);
  const timeComponents = timePart.split(':').map(Number);
  const hours = timeComponents[0] || 0;
  const minutes = timeComponents[1] || 0;
  const seconds = timeComponents[2] || 0;

  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
    return null;
  }

  // Tiempo local deseado interpretado como base UTC pura
  const targetUtcTime = Date.UTC(year, month - 1, day, hours, minutes, seconds);

  try {
    const asIfUtc = targetUtcTime;
    const inTzTime = getPartsUtc(new Date(asIfUtc), tz);
    const offset = inTzTime - asIfUtc;
    const resultTime = asIfUtc - offset;

    // Refinamiento para cambios de horario estacional (DST)
    const check = getPartsUtc(new Date(resultTime), tz);
    const diff = check - targetUtcTime;
    const finalTime = resultTime - diff;

    return new Date(finalTime).toISOString();
  } catch (error) {
    // Fallback defensivo ante identificadores de zona no soportados
    return new Date(dateTimeLocalStr).toISOString();
  }
};

/**
 * Calcula el rango de fechas { startLocal, endLocal, startUtc, endUtc } para presets predefinidos.
 * - 'today': 00:00 a 23:59 del día de hoy local.
 * - 'yesterday': 00:00 a 23:59 del día de ayer local.
 * - 'last6hours': Momento actual menos 6 horas hasta el momento actual.
 * @param {'today'|'yesterday'|'last6hours'|'custom'} presetType - Tipo de preset
 * @param {string} timeZone - Zona horaria de cálculo
 * @returns {{ startLocal: string, endLocal: string, startUtc: string|null, endUtc: string|null }}
 */
const GetPresetRange = (presetType, timeZone) => {
  const tz = timeZone || GetAutodetectedTimezone();
  const now = new Date();

  // Fecha actual en la zona horaria destino
  const currentInTz = formatInTimeZone(now, tz);
  const [currentDatePart] = currentInTz.split('T');
  const [curY, curM, curD] = currentDatePart.split('-').map(Number);

  let startLocal = '';
  let endLocal = '';

  switch (presetType) {
    case 'today': {
      startLocal = `${currentDatePart}T00:00`;
      endLocal = `${currentDatePart}T23:59`;
      break;
    }

    case 'yesterday': {
      // Restar un día en la zona horaria
      const yDateObj = new Date(Date.UTC(curY, curM - 1, curD - 1));
      const yYear = yDateObj.getUTCFullYear();
      const yMonth = String(yDateObj.getUTCMonth() + 1).padStart(2, '0');
      const yDay = String(yDateObj.getUTCDate()).padStart(2, '0');
      const yesterdayDatePart = `${yYear}-${yMonth}-${yDay}`;

      startLocal = `${yesterdayDatePart}T00:00`;
      endLocal = `${yesterdayDatePart}T23:59`;
      break;
    }

    case 'last6hours': {
      // Truncar segundos del momento actual para coincidir con la precisión del input datetime-local
      const nowTrunc = new Date(Math.floor(now.getTime() / 60000) * 60000);
      const sixHoursAgo = new Date(nowTrunc.getTime() - 6 * 60 * 60 * 1000);

      startLocal = formatInTimeZone(sixHoursAgo, tz);
      endLocal = formatInTimeZone(nowTrunc, tz);
      break;
    }

    default:
      return {
        startLocal: '',
        endLocal: '',
        startUtc: null,
        endUtc: null,
      };
  }

  const startUtc = ConvertLocalToUtc(startLocal, tz);
  const endUtc = ConvertLocalToUtc(endLocal, tz);

  return {
    startLocal,
    endLocal,
    startUtc,
    endUtc,
  };
};

/**
 * Valida que el rango de fechas UTC cumpla con el orden cronológico estricto (startUtc <= endUtc).
 * @param {string|null} startUtc - Marca de tiempo ISO UTC inicial
 * @param {string|null} endUtc - Marca de tiempo ISO UTC final
 * @returns {{ isValid: boolean, message?: string }} Resultado de validación
 */
const ValidateDateRange = (startUtc, endUtc) => {
  if (!startUtc || !endUtc) {
    return {
      isValid: false,
      message: 'Debe especificar tanto la fecha y hora de inicio como la de fin.',
    };
  }

  const startTime = new Date(startUtc).getTime();
  const endTime = new Date(endUtc).getTime();

  if (isNaN(startTime) || isNaN(endTime)) {
    return {
      isValid: false,
      message: 'Las fechas seleccionadas no tienen un formato válido.',
    };
  }

  if (startTime > endTime) {
    return {
      isValid: false,
      message: 'La fecha/hora de fin debe ser posterior a la de inicio.',
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Retorna el objeto de filtros normalizado para enviar a los endpoints de la API.
 * En modo 'all' (todo lo acumulado), desactiva y limpia startUtc y endUtc (null)
 * para realizar la consulta o exportación global sin acotamiento temporal.
 * @param {Object} filterState - Estado de filtros
 * @returns {{ startUtc: string|null, endUtc: string|null, timezone: string|null }}
 */
const GetFilterPayload = (filterState = {}) => {
  if (filterState.mode === 'all') {
    return {
      startUtc: null,
      endUtc: null,
      timezone: filterState.timezone || null,
    };
  }

  return {
    startUtc: filterState.startUtc || null,
    endUtc: filterState.endUtc || null,
    timezone: filterState.timezone || null,
  };
};

const pad2 = (val) => String(val).padStart(2, '0');

/**
 * Resuelve el nombre de archivo oficial para el reporte de mensajes.
 * Si el backend proporcionó uno válido, se utiliza dicho nombre.
 * De lo contrario, se construye siguiendo la regla oficial:
 * - Modo 'match': Mensajes_Apoyo_{DDMMYYYY}.xlsx
 * - Modo 'all': Mensajes_Apoyo_Historico_{DDMMYYYY_HHMMSS}.xlsx
 *
 * @param {string|null} backendFileName - Nombre obtenido del header Content-Disposition
 * @param {Object} filter - Objeto con los filtros aplicados
 * @returns {string} Nombre final del archivo con extensión .xlsx
 */
const resolveMessagesFileName = (backendFileName, filter = {}) => {
  if (backendFileName && typeof backendFileName === 'string' && backendFileName.trim()) {
    const cleanName = backendFileName.trim();
    return cleanName.endsWith('.xlsx') ? cleanName : `${cleanName}.xlsx`;
  }

  const now = new Date();
  const nowDD = pad2(now.getDate());
  const nowMM = pad2(now.getMonth() + 1);
  const nowYYYY = now.getFullYear();
  const nowHH = pad2(now.getHours());
  const nowMin = pad2(now.getMinutes());
  const nowSS = pad2(now.getSeconds());

  if (filter.mode === 'all') {
    return `Mensajes_Apoyo_Historico_${nowDD}${nowMM}${nowYYYY}_${nowHH}${nowMin}${nowSS}.xlsx`;
  }

  // Modo 'match': Mensajes_Apoyo_{DDMMYYYY}.xlsx
  let dateStamp = `${nowDD}${nowMM}${nowYYYY}`;
  if (filter.startLocal && typeof filter.startLocal === 'string' && filter.startLocal.includes('T')) {
    const [datePart] = filter.startLocal.split('T');
    const parts = datePart.split('-');
    if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
      dateStamp = `${pad2(parts[2])}${pad2(parts[1])}${parts[0]}`;
    }
  }

  return `Mensajes_Apoyo_${dateStamp}.xlsx`;
};

/**
 * Descarga el reporte consolidado de Mensajes de Apoyo en formato Excel (.xlsx) (HU-03).
 * Invoca GetMessagesExcelBlob, resuelve el nombre oficial, crea una URL temporal
 * con window.URL.createObjectURL(blob), dispara la descarga con un elemento <a>
 * y revoca el object URL.
 *
 * @param {Object} filter - Filtros seleccionados en la UI (startUtc, endUtc, timezone, mode, startLocal, etc.)
 * @returns {Promise<{ success: boolean, fileName?: string, message?: string }>}
 */
const DownloadMessagesExcel = async (filter = {}) => {
  try {
    const response = await GetMessagesExcelBlob(filter);

    if (!response || !response.success || !response.blob) {
      return {
        success: false,
        message: (response && response.message) || 'No fue posible obtener el archivo de reporte.',
      };
    }

    const fileName = resolveMessagesFileName(response.fileName, filter);

    // Crear URL temporal para el blob
    const blobUrl = window.URL.createObjectURL(response.blob);

    // Crear elemento anchor temporal para forzar la descarga
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);

    anchor.click();

    // Limpieza
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(blobUrl);

    return {
      success: true,
      fileName,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Ocurrió un error inesperado al procesar la descarga del reporte.',
    };
  }
};

/**
 * Resuelve el nombre de archivo oficial para el reporte de Fan Cam en ZIP (HU-04).
 * Si el backend proporcionó uno válido en Content-Disposition, se utiliza dicho nombre.
 * De lo contrario, se calcula según el modo de filtrado:
 * - Si hay payload.startUtc: FanCam_Partido_{DDMMYYYY}.zip
 * - Si es acumulado (modo 'all' o sin startUtc): FanCam_Historico_{DDMMYYYY_HHMMSS}.zip
 *
 * @param {string|null} backendFileName - Nombre obtenido del header Content-Disposition
 * @param {Object} payload - Payload normalizado con { startUtc, endUtc, timezone }
 * @param {Object} filter - Objeto original de filtros con { startLocal, mode, ... }
 * @returns {string} Nombre final del archivo con extensión .zip
 */
const resolveFanCamFileName = (backendFileName, payload = {}, filter = {}) => {
  if (backendFileName && typeof backendFileName === 'string' && backendFileName.trim()) {
    const cleanName = backendFileName.trim();
    return cleanName.endsWith('.zip') ? cleanName : `${cleanName}.zip`;
  }

  const now = new Date();
  const nowDD = pad2(now.getDate());
  const nowMM = pad2(now.getMonth() + 1);
  const nowYYYY = now.getFullYear();
  const nowHH = pad2(now.getHours());
  const nowMin = pad2(now.getMinutes());
  const nowSS = pad2(now.getSeconds());

  if (payload.startUtc) {
    let dateStamp = `${nowDD}${nowMM}${nowYYYY}`;
    if (filter.startLocal && typeof filter.startLocal === 'string' && filter.startLocal.includes('T')) {
      const [datePart] = filter.startLocal.split('T');
      const parts = datePart.split('-');
      if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
        dateStamp = `${pad2(parts[2])}${pad2(parts[1])}${parts[0]}`;
      }
    } else {
      const tz = payload.timezone || GetAutodetectedTimezone();
      const localStr = formatInTimeZone(new Date(payload.startUtc), tz);
      const [dPart] = localStr.split('T');
      const parts = dPart.split('-');
      if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
        dateStamp = `${pad2(parts[2])}${pad2(parts[1])}${parts[0]}`;
      }
    }

    return `FanCam_Partido_${dateStamp}.zip`;
  }

  return `FanCam_Historico_${nowDD}${nowMM}${nowYYYY}_${nowHH}${nowMin}${nowSS}.zip`;
};

/**
 * Descarga empaquetada de fotografías de Fan Cam en formato ZIP (HU-04).
 * Extrae el payload normalizado con GetFilterPayload, solicita el blob a la DL,
 * valida el resultado (lanzando un Error si no es exitoso), resuelve el nombre oficial,
 * genera la URL temporal, fuerza la descarga mediante un elemento <a> y revoca la URL.
 *
 * @param {Object} filter - Filtros seleccionados en la UI (startUtc, endUtc, timezone, mode, startLocal, etc.)
 * @returns {Promise<{ success: boolean, fileName: string }>}
 * @throws {Error} Si la petición falla o no se recibe el blob
 */
const DownloadFanCamZip = async (filter = {}) => {
  const payload = GetFilterPayload(filter);
  const result = await GetFanCamZipBlob(payload);

  if (!result || !result.success || !result.blob) {
    const errorMsg =
      (result && (result.error || result.message)) ||
      'No fue posible obtener el archivo ZIP de Fan Cam.';
    throw new Error(errorMsg);
  }

  const fileName = resolveFanCamFileName(result.fileName, payload, filter);

  // Crear URL temporal para el blob
  const blobUrl = window.URL.createObjectURL(result.blob);

  // Crear enlace temporal para forzar la descarga en el navegador
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);

  anchor.click();

  // Limpieza del DOM y revocación de memoria
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(blobUrl);

  return {
    success: true,
    fileName,
  };
};

/**
 * Solicita el inicio de generación en segundo plano del Respaldo Maestro ZIP (HU-05).
 * Normaliza los filtros con GetFilterPayload y realiza la petición asíncrona a la DL.
 *
 * @param {Object} filter - Filtros seleccionados en la UI (startUtc, endUtc, timezone, mode, etc.)
 * @returns {Promise<{ success: boolean, token?: string, fileName?: string, message?: string, isConflict?: boolean }>}
 */
const RequestMasterZip = async (filter = {}) => {
  const payload = GetFilterPayload(filter);
  return await TriggerMasterZipGeneration(payload);
};

/**
 * Descarga en el navegador un archivo de reporte ya generado y disponible vía URL (HU-05).
 * Obtiene el blob mediante DownloadReadyReportBlob, genera una URL temporal con createObjectURL,
 * fuerza la descarga mediante un elemento <a> y revoca la URL de memoria.
 *
 * @param {string} downloadUrl - URL relativa de descarga generada por el backend
 * @param {string} [fallbackFileName='Respaldo_Maestro.zip'] - Nombre de respaldo en caso de ausencia de cabecera
 * @returns {Promise<{ success: boolean, fileName: string }>}
 * @throws {Error} Si no fue posible obtener el blob o la petición falla
 */
const DownloadReadyReport = async (downloadUrl, fallbackFileName = 'Respaldo_Maestro.zip') => {
  const result = await DownloadReadyReportBlob(downloadUrl);

  if (!result || !result.success || !result.blob) {
    const errorMsg =
      (result && result.message) ||
      'No fue posible descargar el archivo de reporte listo.';
    throw new Error(errorMsg);
  }

  const fileName = result.fileName || fallbackFileName;

  // Crear URL temporal para el blob
  const blobUrl = window.URL.createObjectURL(result.blob);

  // Crear enlace temporal para forzar la descarga en el navegador
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);

  anchor.click();

  // Limpieza del DOM y revocación de memoria
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(blobUrl);

  return {
    success: true,
    fileName,
  };
};

export {
  OFFICIAL_TIMEZONES,
  GetAutodetectedTimezone,
  ConvertLocalToUtc,
  GetPresetRange,
  ValidateDateRange,
  GetFilterPayload,
  DownloadMessagesExcel,
  DownloadFanCamZip,
  GetMasterZipStatus,
  RequestMasterZip,
  DownloadReadyReport,
};
